/**
 * Diagnostics — page-instrumentation snapshot the "Report a problem"
 * modal sends with every submission.
 *
 * Goals:
 * 1. Enough detail that a Playwright spec can be auto-generated to
 *    reproduce the exact sequence the visitor performed.
 * 2. Enough detail that an LLM (Claude) can read the report and
 *    propose a likely root cause + fix without bouncing back for
 *    clarification.
 *
 * What we capture:
 *  - breadcrumbs: every click / input / form-submit / navigation /
 *    fetch as it happens, ring-buffered to the last N events.
 *  - errors: window.onerror + unhandledrejection + console.error,
 *    with full stack traces and the component stack when React's
 *    error boundary fires.
 *  - networkLog: fetch + XHR calls (method, url, status, duration).
 *  - domSnapshot: outerHTML of <body> at capture time, sanitized
 *    (password inputs scrubbed, hidden CSRF tokens redacted, very
 *    large images replaced with placeholder data-url so the payload
 *    stays under the backend's 2.5 MB cap).
 *  - state: Redux store snapshot if a `window.__VERO_DIAG_HOOK__`
 *    is registered; otherwise null.
 *  - storage: names only of cookie + localStorage keys — never
 *    values (avoid leaking tokens).
 *
 * Privacy:
 *  - We never record input values for fields that look sensitive
 *    (type=password, autocomplete contains "password" / "cc" /
 *    "credit-card" / "ssn" / "tax"). Their values are replaced
 *    with "<redacted N chars>".
 *  - We never include localStorage / cookie values, only the key
 *    names (so operators can ask "did the visitor have auth_token
 *    set?" without us leaking the token).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BreadcrumbKind =
    | 'click'
    | 'input'
    | 'submit'
    | 'navigation'
    | 'fetch'
    | 'console'
    | 'error';

export interface Breadcrumb {
    kind: BreadcrumbKind;
    at: string;  // ISO timestamp
    /** Short human-readable summary — what the Playwright generator emits as a comment. */
    summary: string;
    /** Structured payload — selectors, URLs, status codes, etc. Schema varies by kind. */
    data: Record<string, unknown>;
}

export interface NetworkLogEntry {
    method: string;
    url: string;
    status: number | null;
    duration_ms: number;
    started_at: string;
    request_body_preview: string | null;
    response_body_preview: string | null;
    error: string | null;
}

export interface ErrorEntry {
    at: string;
    /** "error" | "warn" — matches console levels we trap. */
    level: 'error' | 'warn' | 'uncaught' | 'rejection';
    message: string;
    /** Full stack as a string. */
    stack: string | null;
    /** Source location parsed from window.onerror, if any. */
    source: string | null;
    line: number | null;
    column: number | null;
    /** React component stack when the error came from React's error
     *  boundary. Captured by useErrorBoundary integration. */
    component_stack: string | null;
}

export interface DiagnosticsSnapshot {
    breadcrumbs: Breadcrumb[];
    network_log: NetworkLogEntry[];
    errors: ErrorEntry[];
    dom_snapshot: string | null;
    state: Record<string, unknown> | null;
    cookie_names: string[];
    local_storage_keys: string[];
    session_storage_keys: string[];
}

// ---------------------------------------------------------------------------
// Ring buffers
// ---------------------------------------------------------------------------

const BREADCRUMB_MAX = 100;
const NETWORK_LOG_MAX = 50;
const ERROR_MAX = 50;

const breadcrumbs: Breadcrumb[] = [];
const networkLog: NetworkLogEntry[] = [];
const errors: ErrorEntry[] = [];

function ringPush<T>(arr: T[], item: T, max: number): void {
    arr.push(item);
    if (arr.length > max) arr.splice(0, arr.length - max);
}

function addBreadcrumb(kind: BreadcrumbKind, summary: string, data: Record<string, unknown>): void {
    ringPush(breadcrumbs, {
        kind,
        at: new Date().toISOString(),
        summary,
        data,
    }, BREADCRUMB_MAX);
}

function addError(entry: Omit<ErrorEntry, 'at'>): void {
    ringPush(errors, { ...entry, at: new Date().toISOString() }, ERROR_MAX);
}

// ---------------------------------------------------------------------------
// Selector helpers — what makes a click reproducible
// ---------------------------------------------------------------------------

/**
 * Build the best selector we can for an element so the Playwright
 * generator can target the same node. Priority:
 *   1. data-testid          (project convention)
 *   2. id                   (stable)
 *   3. role + accessible name (durable across redesigns)
 *   4. tagName + nth-of-type fallback
 */
function selectorFor(el: Element | null): string {
    if (!el) return '';
    const testId = el.getAttribute('data-testid');
    if (testId) return `[data-testid="${testId}"]`;
    if (el.id) return `#${CSS.escape(el.id)}`;

    const role = el.getAttribute('role');
    const label = el.getAttribute('aria-label')
        || (el as HTMLElement).innerText?.trim().slice(0, 40)
        || el.getAttribute('title');
    if (role && label) return `role=${role}[name="${label.replace(/"/g, '\\"')}"]`;

    // Tag + nth-of-type relative to parent.
    const tag = el.tagName.toLowerCase();
    const parent = el.parentElement;
    if (!parent) return tag;
    const siblings = Array.from(parent.children).filter(c => c.tagName === el.tagName);
    const idx = siblings.indexOf(el) + 1;
    return `${tag}:nth-of-type(${idx})`;
}

// True for inputs whose value we must NOT capture verbatim.
function isSensitiveInput(el: HTMLInputElement | HTMLTextAreaElement): boolean {
    if ('type' in el && el.type === 'password') return true;
    const hint = `${el.autocomplete ?? ''} ${el.name ?? ''} ${el.id ?? ''}`.toLowerCase();
    return /password|secret|cc-|credit|cvv|ssn|tax/.test(hint);
}

// ---------------------------------------------------------------------------
// Installers — hooks installed once at app start
// ---------------------------------------------------------------------------

let installed = false;

export function installDiagnostics(): void {
    if (installed) return;
    installed = true;

    // -- console ------------------------------------------------------------
    (['error', 'warn'] as const).forEach(level => {
        const original = console[level].bind(console);
        console[level] = (...args: unknown[]) => {
            try {
                addError({
                    level,
                    message: args.map(stringify).join(' '),
                    stack: extractStack(args),
                    source: null, line: null, column: null,
                    component_stack: null,
                });
            } catch { /* never break user code */ }
            original(...args);
        };
    });

    // -- uncaught errors ----------------------------------------------------
    window.addEventListener('error', (e: ErrorEvent) => {
        addError({
            level: 'uncaught',
            message: e.message,
            stack: e.error?.stack ?? null,
            source: e.filename,
            line: e.lineno,
            column: e.colno,
            component_stack: null,
        });
    });
    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
        const r: unknown = e.reason;
        addError({
            level: 'rejection',
            message: r instanceof Error ? r.message : String(r),
            stack: r instanceof Error ? r.stack ?? null : null,
            source: null, line: null, column: null,
            component_stack: null,
        });
    });

    // -- clicks (delegated) -------------------------------------------------
    document.addEventListener('click', (e) => {
        const t = e.target as Element | null;
        if (!t) return;
        // Walk up to the nearest interactive ancestor — clicking the
        // <span> inside a <button> should record the button click.
        const interactive = t.closest('button, a, [role="button"], [role="tab"], [role="menuitem"], input[type="submit"]') || t;
        const sel = selectorFor(interactive);
        const text = (interactive as HTMLElement).innerText?.trim().slice(0, 60) || '';
        addBreadcrumb('click', `Clicked ${text || sel}`, {
            selector: sel,
            text,
            tag: interactive.tagName.toLowerCase(),
            href: interactive instanceof HTMLAnchorElement ? interactive.href : undefined,
        });
    }, { capture: true, passive: true });

    // -- inputs (change events on inputs/textareas) -------------------------
    document.addEventListener('change', (e) => {
        const t = e.target as HTMLInputElement | HTMLTextAreaElement | null;
        if (!t || !(t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement)) return;
        const sel = selectorFor(t);
        let value: string;
        if (isSensitiveInput(t)) {
            value = `<redacted ${(t.value || '').length} chars>`;
        } else if (t instanceof HTMLInputElement && t.type === 'checkbox') {
            value = String(t.checked);
        } else {
            value = (t.value ?? '').slice(0, 200);
        }
        addBreadcrumb('input', `Set ${t.name || sel} = ${value.length > 30 ? value.slice(0, 30) + '…' : value}`, {
            selector: sel,
            name: t.name,
            type: 'type' in t ? t.type : 'textarea',
            value,
        });
    }, { capture: true, passive: true });

    // -- form submits -------------------------------------------------------
    document.addEventListener('submit', (e) => {
        const t = e.target as HTMLFormElement | null;
        if (!t) return;
        addBreadcrumb('submit', `Submitted ${selectorFor(t)}`, {
            selector: selectorFor(t),
            action: t.action,
            method: t.method,
        });
    }, { capture: true, passive: true });

    // -- navigation (pushState + popstate) ----------------------------------
    // React Router uses history.pushState; we wrap both pushState and
    // replaceState so SPA navigation is captured.
    const wrapHistory = (method: 'pushState' | 'replaceState') => {
        const original = history[method];
        history[method] = function (this: History, data: unknown, unused: string, url?: string | URL | null) {
            const result = original.apply(this, [data, unused, url] as Parameters<typeof original>);
            addBreadcrumb('navigation', `Navigated to ${String(url ?? location.pathname)}`, {
                method, url: String(url ?? location.pathname),
            });
            return result;
        };
    };
    wrapHistory('pushState');
    wrapHistory('replaceState');
    window.addEventListener('popstate', () => {
        addBreadcrumb('navigation', `popstate → ${location.pathname}`, {
            method: 'popstate', url: location.pathname,
        });
    });
    // Seed the breadcrumb buffer with the initial page so the first
    // entry is always a "started on /foo" marker.
    addBreadcrumb('navigation', `Started on ${location.pathname}`, {
        method: 'initial', url: location.pathname,
    });

    // -- fetch wrapper ------------------------------------------------------
    const origFetch = window.fetch.bind(window);
    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
        const started = performance.now();
        const startedAt = new Date().toISOString();
        const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
        const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
        const reqPreview = typeof init?.body === 'string' ? init.body.slice(0, 400) : null;
        try {
            const res = await origFetch(input as RequestInfo, init);
            const duration = Math.round(performance.now() - started);
            ringPush(networkLog, {
                method, url, status: res.status, duration_ms: duration,
                started_at: startedAt,
                request_body_preview: reqPreview,
                // Don't clone+read large response bodies on every call
                // — too expensive. The status code + URL is usually
                // enough for repro, and the response body shows up
                // in the breadcrumb summary on errors.
                response_body_preview: res.status >= 400 ? await safeReadResponsePreview(res) : null,
                error: null,
            }, NETWORK_LOG_MAX);
            addBreadcrumb('fetch', `${method} ${shorten(url)} → ${res.status} (${duration}ms)`, {
                method, url, status: res.status, duration_ms: duration,
            });
            return res;
        } catch (err) {
            const duration = Math.round(performance.now() - started);
            ringPush(networkLog, {
                method, url, status: null, duration_ms: duration,
                started_at: startedAt,
                request_body_preview: reqPreview,
                response_body_preview: null,
                error: err instanceof Error ? err.message : String(err),
            }, NETWORK_LOG_MAX);
            addBreadcrumb('fetch', `${method} ${shorten(url)} → ERROR`, {
                method, url, error: err instanceof Error ? err.message : String(err),
            });
            throw err;
        }
    };

    // -- XHR wrapper --------------------------------------------------------
    // axios + the LiveKit SDK use XMLHttpRequest, not fetch, so we
    // hook this too. Minimal — we just track method/url/status/duration.
    const OrigXHR = XMLHttpRequest;
    function PatchedXHR(this: XMLHttpRequest) {
        const xhr = new OrigXHR();
        let xhrMethod = 'GET';
        let xhrUrl = '';
        let startTime = 0;

        const origOpen = xhr.open.bind(xhr);
        xhr.open = function (
            method: string,
            url: string | URL,
            isAsync?: boolean,
            username?: string | null,
            password?: string | null,
        ): void {
            xhrMethod = method.toUpperCase();
            xhrUrl = typeof url === 'string' ? url : url.toString();
            // Forward to the overload the caller used: the longer form
            // can switch the request to synchronous mode.
            return isAsync === undefined
                ? origOpen(method, url)
                : origOpen(method, url, isAsync, username, password);
        };

        const origSend = xhr.send.bind(xhr);
        xhr.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
            startTime = performance.now();
            const startedAt = new Date().toISOString();
            xhr.addEventListener('loadend', () => {
                const duration = Math.round(performance.now() - startTime);
                ringPush(networkLog, {
                    method: xhrMethod, url: xhrUrl,
                    status: xhr.status || null,
                    duration_ms: duration,
                    started_at: startedAt,
                    request_body_preview: typeof body === 'string' ? body.slice(0, 400) : null,
                    response_body_preview: null,
                    error: xhr.status === 0 ? 'XHR error or aborted' : null,
                }, NETWORK_LOG_MAX);
                addBreadcrumb('fetch', `${xhrMethod} ${shorten(xhrUrl)} → ${xhr.status} (${duration}ms)`, {
                    method: xhrMethod, url: xhrUrl, status: xhr.status, duration_ms: duration, transport: 'xhr',
                });
            });
            return origSend(body);
        };
        return xhr;
    }
    PatchedXHR.prototype = OrigXHR.prototype;
    // @ts-expect-error - assigning a function constructor over a class
    window.XMLHttpRequest = PatchedXHR;
}

// ---------------------------------------------------------------------------
// Snapshot — called by the report modal at submission time
// ---------------------------------------------------------------------------

export function captureSnapshot(): DiagnosticsSnapshot {
    return {
        breadcrumbs: [...breadcrumbs],
        network_log: [...networkLog],
        errors: [...errors],
        dom_snapshot: sanitizedDomSnapshot(),
        state: readStateHook(),
        cookie_names: parseCookieNames(),
        local_storage_keys: keysOf(localStorage),
        session_storage_keys: keysOf(sessionStorage),
    };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stringify(v: unknown): string {
    if (v instanceof Error) return `${v.name}: ${v.message}`;
    if (typeof v === 'object' && v !== null) {
        try { return JSON.stringify(v); } catch { return String(v); }
    }
    return String(v);
}

function extractStack(args: unknown[]): string | null {
    for (const a of args) if (a instanceof Error && a.stack) return a.stack;
    return null;
}

function shorten(url: string): string {
    if (url.length <= 80) return url;
    return url.slice(0, 50) + '…' + url.slice(-25);
}

async function safeReadResponsePreview(res: Response): Promise<string | null> {
    try {
        const clone = res.clone();
        const text = await clone.text();
        return text.slice(0, 600);
    } catch {
        return null;
    }
}

function parseCookieNames(): string[] {
    return document.cookie
        .split(';')
        .map(c => (c.split('=')[0] ?? '').trim())
        .filter(Boolean);
}

function keysOf(s: Storage): string[] {
    const out: string[] = [];
    for (let i = 0; i < s.length; i++) {
        const k = s.key(i);
        if (k) out.push(k);
    }
    return out;
}

function readStateHook(): Record<string, unknown> | null {
    // Apps can register an inspector that returns a redacted snapshot
    // of their Redux store / auth context / whatever else is useful
    // for debugging. We don't reach into Redux directly to avoid
    // coupling diagnostics to the store shape.
    const w = window as Window & { __VERO_DIAG_STATE_HOOK__?: () => Record<string, unknown> };
    try {
        return w.__VERO_DIAG_STATE_HOOK__?.() ?? null;
    } catch {
        return null;
    }
}

function sanitizedDomSnapshot(): string | null {
    // Clone the body, scrub sensitive bits, return outerHTML.
    try {
        const clone = document.body.cloneNode(true) as HTMLElement;

        // Redact password inputs.
        clone.querySelectorAll('input[type="password"]').forEach(el => {
            (el as HTMLInputElement).setAttribute('value', '<redacted>');
        });

        // Redact CSRF tokens (any hidden input named csrf*).
        clone.querySelectorAll('input[type="hidden"]').forEach(el => {
            const inp = el as HTMLInputElement;
            if (/csrf|token|secret/i.test(inp.name || '')) {
                inp.setAttribute('value', '<redacted>');
            }
        });

        // Replace large data: image URLs with a placeholder so the
        // snapshot stays sub-MB.
        clone.querySelectorAll('img').forEach(el => {
            const img = el as HTMLImageElement;
            if (img.src.startsWith('data:') && img.src.length > 2000) {
                img.src = 'data:image/png;base64,REDACTED_LARGE_DATAURL';
            }
        });

        // Strip <script> contents — we don't want to echo a bundle
        // back to the server.
        clone.querySelectorAll('script').forEach(el => {
            el.textContent = '/* stripped */';
        });

        // Cap final size — anything above 800KB gets truncated with
        // a marker so the backend's 2.5MB cap isn't blown by a
        // single huge page.
        let html = clone.outerHTML;
        const MAX = 800_000;
        if (html.length > MAX) {
            html = html.slice(0, MAX) + `\n<!-- TRUNCATED ${html.length - MAX} chars -->`;
        }
        return html;
    } catch {
        return null;
    }
}

// ---------------------------------------------------------------------------
// Public read API — small surface for the modal + tests
// ---------------------------------------------------------------------------

export function _internal_state(): {
    breadcrumbs: Breadcrumb[];
    errors: ErrorEntry[];
    networkLog: NetworkLogEntry[];
} {
    return { breadcrumbs, errors, networkLog };
}
